import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import FilterSidebarFavoriteButton from "./FavoriteButton/FilterSidebarFavoriteButton";
import { ResourceLibraryMetadataFilters } from "@/modules/hub/features/ResourceLibrary/api/getResourceLibratyMetadata/types";
import { Input } from "@/components/ui/input";
import { Filter as DefaultFilterIcon } from "lucide-react";
import { PartnerNetworkFilterFormData } from "../../hooks/PartnerNetworkFilterFormSchema";
import { IconSvg } from "./IconSvg/IconSvg";

interface FilterSidebarFormProps {
  loading?: boolean;
  onSubmit: (values: PartnerNetworkFilterFormData) => void;
  form: UseFormReturn<PartnerNetworkFilterFormData>;
  filters: ResourceLibraryMetadataFilters | null;
}

const FilterSidebarForm: React.FC<FilterSidebarFormProps> = ({
  loading = false,
  onSubmit,
  form,
  filters,
}) => {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = form;

  const [showAllCategories, setShowAllCategories] = useState(false);

  const availableTrades = filters?.trades ?? [];
  const availableCategories = filters?.resourceCategories ?? [];

  const visibleCategories = showAllCategories
    ? availableCategories
    : availableCategories.slice(0, 3);

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <FormField
          control={control}
          name="isFavoriteOnly"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Favorite</FormLabel>
              <FormControl>
                <FilterSidebarFavoriteButton
                  onChange={field.onChange}
                  value={field.value || false}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="trades"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Trades</FormLabel>
              <FormControl>
                <div className="space-y-2">
                  {availableTrades.map((trade) => {
                    const isSelected = field.value?.some(
                      (item) => item.id === trade.id,
                    );

                    const icon = trade.icon ? (
                      <IconSvg
                        className="w-4 h-4 text-gray-600 dark:text-slate-400 [&>svg]:w-full [&>svg]:h-full [&>svg]:stroke-current [&>svg]:fill-none"
                        svg={trade.icon}
                      />
                    ) : (
                      <DefaultFilterIcon className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                    );

                    return (
                      <button
                        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
                          isSelected
                            ? "bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60"
                            : "bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700"
                        }`}
                        key={trade.id}
                        onClick={() => {
                          const selected = [...(field.value || [])];
                          const index = selected.findIndex(
                            (item) => item.id === trade.id,
                          );

                          if (index >= 0) {
                            selected.splice(index, 1);
                          } else {
                            selected.push({
                              id: trade.id,
                              name: trade.name,
                              icon: trade.icon,
                            });
                          }

                          field.onChange(selected);
                        }}
                        type="button"
                      >
                        <div className="flex items-center gap-2">
                          {icon}
                          <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                            {trade.name}
                          </span>
                        </div>
                        {trade.count !== undefined && (
                          <span
                            className={`w-5 h-5 flex items-center justify-center text-xs rounded-full font-medium ${
                              isSelected
                                ? "bg-blue-500 text-white"
                                : "bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-400"
                            }`}
                          >
                            {trade.count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="businessRoles"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Job Title</FormLabel>
              <FormControl>
                <div className="space-y-2">
                  {(filters?.businessRoles ?? []).map((role) => {
                    const isSelected = field.value?.some(
                      (item) => item.id === role.id,
                    );

                    return (
                      <label
                        className="flex items-center justify-between gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer"
                        key={role.id}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            checked={isSelected}
                            className="accent-primary h-4 w-4"
                            onChange={(e) => {
                              const { checked } = e.target;
                              const currentValue = [...(field.value || [])];

                              if (checked) {
                                if (!isSelected) {
                                  currentValue.push({
                                    id: role.id,
                                    name: role.name,
                                    description: role.description,
                                  });
                                }
                              } else {
                                const index = currentValue.findIndex(
                                  (item) => item.id === role.id,
                                );
                                if (index >= 0) {
                                  currentValue.splice(index, 1);
                                }
                              }

                              field.onChange(currentValue);
                            }}
                            type="checkbox"
                          />
                          {role.name}
                        </div>
                        {(role as any).count !== undefined && (
                          <span className="text-xs text-gray-500 dark:text-slate-500">
                            ({(role as any).count})
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {availableCategories.length > 0 && (
          <FormField
            control={control}
            name="categories"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Categories</FormLabel>
                <FormControl>
                  <>
                    <div className="space-y-2">
                      {visibleCategories.map((cat) => {
                        const isSelected = field.value?.some(
                          (item) => item.id === cat.id,
                        );

                        return (
                          <label
                            className="flex items-center justify-between gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer"
                            key={cat.id}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                checked={isSelected}
                                className="accent-primary h-4 w-4"
                                onChange={(e) => {
                                  const { checked } = e.target;
                                  const currentValue = [...(field.value || [])];

                                  if (checked && !isSelected) {
                                    currentValue.push({
                                      id: cat.id,
                                      name: cat.name,
                                    });
                                  } else if (!checked) {
                                    const index = currentValue.findIndex(
                                      (item) => item.id === cat.id,
                                    );
                                    if (index >= 0) {
                                      currentValue.splice(index, 1);
                                    }
                                  }

                                  field.onChange(currentValue);
                                }}
                                type="checkbox"
                              />
                              {cat.name}
                            </div>
                            {(cat as any).count !== undefined && (
                              <span className="text-xs text-gray-500 dark:text-slate-500">
                                ({(cat as any).count})
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>

                    {availableCategories.length > 3 && (
                      <div className="mt-2">
                        <button
                          className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
                          onClick={() => setShowAllCategories((prev) => !prev)}
                          type="button"
                        >
                          {showAllCategories ? "View less" : "View more"}
                        </button>
                      </div>
                    )}
                  </>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </form>
    </Form>
  );
};

export default FilterSidebarForm;
